import React, { useState } from 'react';
import {
  Eye, Edit, Trash2, FileText, AlertCircle, Check, X,
} from 'lucide-react';
import { usePersonalActions } from '../../../hooks/usePersonalActions';
import Pagination from '../../common/Pagination';

const POR_PAGINA = 12;

const getCategoriaCls = (cat) => {
  const map = {
    administrativa: 'bg-blue-100 text-blue-700',
    disciplinaria:  'bg-red-100 text-red-700',
    contractual:    'bg-blue-100 text-blue-700',
    salarial:       'bg-green-100 text-green-700',
  };
  return map[cat] || 'bg-gray-100 text-gray-600';
};

const getEstadoBadge = (estado) => {
  const map = {
    pendiente:  { bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500',  label: 'Pendiente'  },
    aprobado:   { bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500',  label: 'Aprobado'   },
    rechazado:  { bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500',    label: 'Rechazado'  },
    revocado:   { bg: 'bg-gray-100',   text: 'text-gray-600',   dot: 'bg-gray-400',   label: 'Revocado'   },
    completado: { bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500',   label: 'Completado' },
  };
  return map[estado] || map.completado;
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('es-DO', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function PersonalActionsList({ actions, onEdit, onView, onDeleteSuccess }) {
  const { deleteAction, approveAction, rejectAction } = usePersonalActions();
  const [page, setPage] = useState(1);

  const handleApprove = async (action) => {
    if (!window.confirm(`¿Aprobar la acción "${action.titulo}"?\n\nEsto aplicará los cambios al contrato del empleado.`)) return;
    const r = await approveAction(action.id);
    if (r.success) { alert('Acción aprobada y aplicada exitosamente'); onDeleteSuccess(); }
    else alert('Error al aprobar acción: ' + r.error);
  };

  const handleReject = async (action) => {
    const motivo = prompt('Motivo del rechazo:');
    if (!motivo) return;
    if (!window.confirm(`¿Rechazar la acción "${action.titulo}"?`)) return;
    const r = await rejectAction(action.id, motivo);
    if (r.success) { alert('Acción rechazada'); onDeleteSuccess(); }
    else alert('Error: ' + r.error);
  };

  const handleDelete = async (action) => {
    const motivo = prompt('Motivo de la revocación:');
    if (!motivo) return;
    if (!window.confirm(`¿Revocar la acción "${action.titulo}"?`)) return;
    const r = await deleteAction(action.id, motivo);
    if (r.success) { alert('Acción revocada'); onDeleteSuccess(); }
    else alert('Error: ' + r.error);
  };

  if (actions.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-16 text-center">
        <FileText size={40} className="text-gray-200 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-500">No hay acciones de personal registradas</p>
        <p className="text-xs text-gray-400 mt-1">Crea una nueva acción para comenzar</p>
      </div>
    );
  }

  const totalPages = Math.ceil(actions.length / POR_PAGINA);
  const start      = (page - 1) * POR_PAGINA;
  const current    = actions.slice(start, start + POR_PAGINA);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">

          {/* ── Head ─────────────────────────────────────────────── */}
          <thead>
            <tr className="border-b border-gray-200">
              <th className="w-10 px-4 py-3">
                <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600 cursor-pointer" disabled />
              </th>
              {['EMPLEADO', 'TIPO / N°', 'TÍTULO', 'FECHA', 'CATEGORÍA', 'ESTADO', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          {/* ── Body ─────────────────────────────────────────────── */}
          <tbody className="divide-y divide-gray-100">
            {current.map((action) => {
              const badge    = getEstadoBadge(action.estado);
              const initials = action.empleado_nombre?.charAt(0)?.toUpperCase() || '?';

              return (
                <tr
                  key={action.id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer group"
                  onClick={() => onView(action)}
                >
                  {/* Checkbox */}
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600 cursor-pointer" />
                  </td>

                  {/* Empleado */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        {action.empleado_foto ? (
                          <img
                            src={action.empleado_foto}
                            alt={action.empleado_nombre}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div
                          className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ display: action.empleado_foto ? 'none' : 'flex' }}
                        >
                          {initials}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{action.empleado_nombre}</p>
                        <p className="text-xs text-gray-400">{action.empleado_codigo}</p>
                      </div>
                    </div>
                  </td>

                  {/* Tipo / N° */}
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-700">{action.tipo_accion_nombre}</p>
                    {action.numero_accion && (
                      <p className="text-xs font-mono text-blue-600">{action.numero_accion}</p>
                    )}
                  </td>

                  {/* Título */}
                  <td className="px-4 py-3 max-w-[200px]">
                    <p className="text-sm text-gray-900 truncate">{action.titulo}</p>
                  </td>

                  {/* Fecha */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <p className="text-xs text-gray-600">{formatDate(action.fecha_efectiva)}</p>
                    {action.fecha_fin && (
                      <p className="text-xs text-gray-400">hasta {formatDate(action.fecha_fin)}</p>
                    )}
                  </td>

                  {/* Categoría */}
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoriaCls(action.tipo_accion_categoria)}`}>
                      {action.tipo_accion_categoria || 'Sin categoría'}
                    </span>
                  </td>

                  {/* Estado */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${badge.bg} ${badge.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                      {badge.label}
                    </span>
                    <p className="text-xs text-gray-400 mt-0.5">{action.creado_por || 'Sistema'}</p>
                  </td>

                  {/* Acciones */}
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onView(action)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Ver detalles"
                      >
                        <Eye size={14} />
                      </button>

                      {action.estado === 'pendiente' && (
                        <>
                          <button
                            onClick={() => handleApprove(action)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Aprobar"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => handleReject(action)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Rechazar"
                          >
                            <X size={14} />
                          </button>
                        </>
                      )}

                      {action.estado === 'aprobado' && (
                        <>
                          <button
                            onClick={() => onEdit(action)}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(action)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Revocar"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}

                      {(action.estado === 'rechazado' || action.estado === 'revocado') && (
                        <AlertCircle size={14} className="text-gray-300 ml-1" title={`Acción ${action.estado}`} />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={actions.length}
        pageSize={POR_PAGINA}
        onPageChange={setPage}
      />
    </div>
  );
}
